import { Type } from '@sinclair/typebox';
import { Value } from '@sinclair/typebox/value';
import { createFileRoute, useLocation } from '@tanstack/react-router';
import { useFlagsStatus } from '@unleash/proxy-client-react';
import { useEffect } from 'react';
import { Alert } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';

import { ReportError, useQuery } from '@furality/frontend-stack';
import fox from '#/api/fox.ts';
import processEvents from '@/components/_schedule/processEvents.ts';
import ScheduleView from '@/components/_schedule/ScheduleView.tsx';
import ScheduleLayout from '@/components/layouts/ScheduleLayout.tsx';
import PageLoading from '@/components/loading/PageLoading.tsx';
import PageDisabled from '@/components/PageDisabled.tsx';
import ScrollToTopStickyButton from '@/components/ScrollToTopStickyButton.tsx';
import useEventReferrer from '@/lib/helpers/useEventReferrer.ts';
import useFlag from '@/lib/hooks/useFlag.ts';
import QUERY_KEYS from '@/lib/QUERY_KEYS.ts';

// biome-ignore lint/style/useComponentExportOnlyModules: tanstack route
const Schedule = () => {
  const { t } = useTranslation();
  const location = useLocation();

  const featuresReady = useFlagsStatus().flagsReady;
  const enableSchedule = useFlag('schedule');

  const events = useQuery({
    queryKey: [QUERY_KEYS.Schedule],
    queryFn: async () => {
      const response = await fox.value.eventFindAll();
      switch (response.status) {
        case 200:
          return processEvents(response.data.data);
        default:
          throw new ReportError(response);
      }
    },
    enabled: enableSchedule,
    refetchInterval: 1000 * 60 * 15, // 15 min
  });

  const active = Route.useSearch({ select: (s) => s.active });
  const [, setEventReferrer] = useEventReferrer();
  useEffect(() => {
    setEventReferrer({
      link: {
        to: '/schedule',
        search: {
          active,
        },
      },
    });
  }, [location.pathname, location.searchStr]);

  if (!featuresReady) {
    return <PageLoading />;
  }

  if (!enableSchedule) {
    return <PageDisabled />;
  }

  return (
    <div className="slice">
      {events.isLoading && <PageLoading />}
      <div className="mb-5 text-center">
        <h1>{t('shared.navigation.schedule')}</h1>
      </div>
      {events.isSuccess && <ScheduleView events={events.data} />}
      {events.isError && (
        <Alert variant="danger">{t('f7.shared.errors.unknown_error_try_later')}</Alert>
      )}
      <ScrollToTopStickyButton />
    </div>
  );
};

const RouteSearch = Type.Object({
  active: Type.Optional(Type.String()),
});
export const Route = createFileRoute('/schedule')({
  component: () => (
    <ScheduleLayout title="shared.navigation.schedule" fluid="md">
      <Schedule />
    </ScheduleLayout>
  ),
  validateSearch: (search) => Value.Cast(RouteSearch, search),
});
